import { useQuery } from "@tanstack/vue-query";
import { computed, type MaybeRefOrGetter, toValue } from "vue";
import { request } from "@/shared/http/client";

/**
 * Achar uma coisa pelo nome dela, em UMA pergunta.
 *
 * ── POR QUE UMA ROTA SÓ ───────────────────────────────────────────────────
 *
 * Porque a alternativa é a paleta abrir um leque: uma chamada para contatos, uma
 * para conversas e uma por entidade ativa do workspace — que é um número que o
 * tenant escolhe, não a plataforma. `GET /v3/search` responde as três origens de
 * uma vez, e quem sabe quais entidades existem é o servidor, no instante do
 * pedido.
 *
 * ── POR QUE O `to` VEM PRONTO ─────────────────────────────────────────────
 *
 * Para a paleta não remontar rota. Um resultado sabe onde mora (`/contacts/:id`,
 * `/inbox/:id`, `/e/:code/:id`) e o front só empurra a string para o router — do
 * contrário haveria um segundo lugar decidindo o endereço de um registro, que é
 * exatamente o desacordo que a Fase 3 existe para acabar.
 */
export type SearchKind = "contact" | "conversation" | "record";

export interface SearchHit {
	readonly kind: SearchKind;
	readonly id: string;
	/** O que a pessoa reconhece: o nome, o assunto, o título do registro. */
	readonly label: string;
	/** A segunda linha curta — o telefone, o canal, a entidade. Nem tudo tem uma. */
	readonly hint: string | null;
	/** Caminho do painel, montado pelo servidor. */
	readonly to: string;
	/**
	 * A ETAPA deste registro — o CÓDIGO dela, nunca o rótulo.
	 *
	 * Decisão de 23/08 ("a etapa é o registro"): a coluna de espinha volta com toda
	 * linha, então dizer em que etapa um resultado está não custa consulta nenhuma.
	 * Quem vira o código em palavra é a paleta, que já carrega os funis para
	 * oferecê-los como destino — pedir o rótulo ao servidor faria ele ler o funil de
	 * cada entidade a cada tecla, para escrever o que o cliente já tem na mão.
	 */
	readonly stage?: string | null;
	/** De qual entidade a linha veio — é por ele que se acha o funil que nomeia a etapa. */
	readonly entityCode?: string | null;
}

export interface SearchAnswer {
	readonly items: readonly SearchHit[];
	/**
	 * Em quantas telas do workspace o servidor conseguiu procurar, de quantas
	 * existem. Ele para em doze porque doze entidades são doze tabelas, e diz que
	 * parou — um resultado que pulou metade do workspace em silêncio é pior do que
	 * um que avisa.
	 */
	readonly entities: { readonly searched: number; readonly active: number };
	/**
	 * Quantas linhas CASARAM por seção — não quantas couberam.
	 *
	 * Sem isto a paleta escrevia "8 de 8" com duzentos contatos casando, que é a
	 * linha que faz alguém concluir que só existem oito. Opcional porque um bundle
	 * novo contra uma API velha ainda tem de desenhar a lista: ausente significa
	 * "não sei", e quem lê cai no tamanho da própria seção em vez de escrever zero.
	 */
	readonly totals?: {
		readonly contact: number;
		readonly conversation: number;
		readonly record: number;
	};
	/** Quanto o servidor levou, em ms — o número do canto do rodapé, como na V2. */
	readonly tookMs?: number;
}

/**
 * Menos que isto devolve meio cadastro e pisca a cada tecla.
 *
 * O `ContactPicker` pede duas letras porque busca dentro de um cadastro só; esta
 * varre três origens, então a terceira letra é o que separa uma resposta de uma
 * lista qualquer.
 */
export const MIN_TERM = 3;

export const searchKeys = {
	query: (term: string) => ["search", term] as const,
};

export function useSearch(term: MaybeRefOrGetter<string>) {
	const needle = computed(() => toValue(term).trim());
	const asked = computed(() => needle.value.length >= MIN_TERM);

	const query = useQuery({
		enabled: asked,
		queryKey: computed(() => searchKeys.query(needle.value)),
		queryFn: async (): Promise<SearchAnswer> =>
			request(`/v3/search?q=${encodeURIComponent(needle.value)}`),
		// O mesmo termo digitado de novo dentro do minuto não vale uma ida ao
		// servidor: quem apaga e redigita está corrigindo o dedo, não a pergunta.
		staleTime: 60_000,
	});

	return { ...query, asked };
}

/** Uma peça de "Seu sistema" — o recorte do mapa que a paleta lê. */
export interface SystemPiece {
	/** A chave que `/system?no=` seleciona (`entity:x`, `view:x.y`, `automation:Nome`…). */
	readonly key: string;
	readonly kind: string;
	readonly code: string;
	readonly name: string;
	readonly parent: string | null;
	/** Numa tela, a espécie dela — `component` é a tela sob medida. */
	readonly detail?: string;
	/** Peça que a plataforma gerou sozinha (a entrada de um canal…), não a do construtor. */
	readonly generated?: boolean;
}

/**
 * AS PEÇAS DO SISTEMA, pelo nome — automações, funções, atendentes, relatórios,
 * telas. Antes o ⌘K só achava pessoas, conversas e registros: "agend" não trazia
 * a automação nem a tela "Calendário de agendamentos", e "Luana" não trazia a
 * atendente.
 *
 * O mapa de "Seu sistema" (`GET /v3/system/map`) já tem cada peça com o nome e a
 * chave que abre ela, e é só de administrador — a mesma tranca de quem pode abrir
 * a peça. Lido uma vez quando a paleta abre e peneirado aqui: são dezenas de
 * linhas, e uma ida ao servidor por tecla para as mesmas dezenas seria desperdício.
 * A chave do cache é a do próprio mapa, então a paleta e "Seu sistema" partilham a
 * mesma leitura e a mesma invalidação.
 */
export function useSystemPieces(enabled: MaybeRefOrGetter<boolean>) {
	return useQuery({
		enabled: computed(() => toValue(enabled)),
		queryKey: ["system", "map"] as const,
		queryFn: () => request<{ nodes: readonly SystemPiece[] }>("/v3/system/map"),
		staleTime: 15_000,
		select: (map) => map.nodes,
	});
}
